// 스타일 카탈로그 + 분석 프롬프트용 표 — **앱과 서버가 같이 쓰는 원본.**
// 순수 데이터만 둔다 (import 금지). functions/shared/style-catalog.js 는
// scripts/sync-shared.mjs 가 이 파일로 만드는 CommonJS 사본이다 — 거기를 고치지
// 말 것. functions 배포 전에 자동으로 돈다 (firebase.json predeploy).
//
// 스타일을 추가하면 앱 빌드가 필요하다 (목록·썸네일이 앱에 있다). 서버 쪽
// Gio 추천·서버 생성 경로는 다음 functions 배포 때 같이 알게 된다.

// Budget tiers act as a secondary hint layered on top of style. They describe
// price range and material quality — NOT specific brands. This matters because
// some styles (e.g. IKEA, Classic) already imply a price tier, and naming
// brands like CB2 or Design Within Reach in the prompt would conflict. Letting
// the AI reconcile style + tier within the chosen aesthetic keeps the system
// coherent across all style choices.
export const BUDGET_TIERS = ['budget', 'mid', 'luxury'];

export const BUDGET_TIER_DESCRIPTION = {
  budget: 'entry-level, affordable, DIY-friendly; prioritize practical and accessible pieces within the chosen style',
  mid: 'mid-range quality, everyday durability; balance value and design within the chosen style',
  luxury: 'premium, designer, investment-grade pieces; lean toward higher-quality materials and finer finishes within the chosen style',
};

// Generation mode. The user no longer picks this — the AI auto-detects
// whether the input is empty/vacant ('staging' = furnish from scratch) or
// already furnished ('redesign' = transform in place). `detectedMode` is
// returned in the response and saved to the design doc for display only.
export const DESIGN_MODES = ['redesign', 'staging'];

// Space category (Phase 9-5). Routes which style set + prompt framing the
// design uses. `interior` is the original (default) flow.

export const SPACE_CATEGORIES = ['interior', 'exterior', 'garden'];

// Detected input types (Phase 9-6, auto-detect). The text pass classifies the
// uploaded image so the image pass and the UI can branch accordingly. We do
// NOT expose this as a user-facing toggle — the AI decides.
export const DETECTED_INPUT_TYPES = ['photo', 'sketch', 'elevation', 'floorplan', '3d_model'];

// Exterior style set — facades, building envelopes, balconies.
export const EXTERIOR_STYLES = {
  modern_facade: {
    name: 'Modern Facade',
    description: 'Clean geometry, large glass, mixed materials',
    keywords: 'modern facade, large windows, mixed cladding, flat roof, neutral palette',
  },
  modern_farmhouse: {
    name: 'Modern Farmhouse',
    description: 'White siding, black trim, wood accents',
    keywords: 'modern farmhouse, white siding, black trim, gable roof, wood accents',
  },
  mediterranean: {
    name: 'Mediterranean',
    description: 'Stucco, terracotta, arched openings',
    keywords: 'mediterranean, stucco, terracotta tile, arches, wrought iron, warm earth tones',
  },
  craftsman: {
    name: 'Craftsman',
    description: 'Wood-shingled, low-pitched gables, exposed rafters',
    keywords: 'craftsman, wood shingles, exposed rafters, columned porch, earthy palette',
  },
  contemporary: {
    name: 'Contemporary',
    description: 'Sharp lines, bold volumes, industrial materials',
    keywords: 'contemporary, concrete, steel, sharp angles, bold volumes',
  },
  minimalist_exterior: {
    name: 'Minimalist Exterior',
    description: 'Restrained palette, single-material focus, simple form',
    keywords: 'minimalist exterior, single material, restrained palette, simple cubic forms',
  },
  tudor: {
    name: 'Tudor',
    description: 'English Tudor revival — half-timbered with steep gables',
    keywords: 'tudor revival, half-timbered, dark timber framing, white stucco, steep gable roof, leaded glass windows, brick chimney',
  },
  cape_cod: {
    name: 'Cape Cod',
    description: 'New England traditional — white shingle with black shutters',
    keywords: 'cape cod, white cedar shingle, black shutters, gable roof, dormer windows, picket fence, american traditional',
  },
  japanese_traditional: {
    name: 'Japanese Traditional',
    description: 'Minka-style wood frame with tiled roof and engawa',
    keywords: 'minka, japanese traditional house, wood post and beam, kawara tile roof, shoji, engawa porch, sliding doors, zen courtyard',
  },
  mountain_cabin: {
    name: 'Mountain Cabin',
    description: 'Timber A-frame or log cabin nestled in nature',
    keywords: 'mountain cabin, log cabin, a-frame, timber, stone chimney, steep pitched roof, alpine, wood siding, cozy retreat',
  },
  midcentury_exterior: {
    name: 'Mid-Century Exterior',
    description: '1950s–60s modernism — flat roof, big glass, horizontal lines',
    keywords: 'mid-century modern exterior, flat roof, low-pitched, large glass walls, horizontal lines, palm springs, eichler, post and beam',
  },

  // ── 시즌 스타일 ────────────────────────────────────────────────────
  // interiorStyles 와 같은 창(window). 노출 판정은 AIService.isInSeason 이
  // 하고, 창은 Firestore config/seasons 로 덮어쓸 수 있다.
  // **키는 카테고리 간 동일해야 한다** — config/seasons 가 키 하나로 세
  // 카테고리를 한꺼번에 켜고 끈다.
  halloween: {
    name: 'Halloween',
    description: 'Carved pumpkins, lanterns, moody autumn porch',
    keywords: 'halloween house exterior, carved pumpkins on steps, dried corn stalks, string lanterns, black and amber, misty autumn dusk, seasonal decor',
    season: { from: '09-15', to: '11-02' }
  },
  christmas: {
    name: 'Christmas',
    description: 'Wreaths, warm string lights, evergreen garland',
    keywords: 'christmas house exterior, evergreen wreath on door, warm white string lights along eaves, garland on railing, snow dusting, festive facade, seasonal decor',
    season: { from: '11-20', to: '12-31' }
  },
  lunar_new_year: {
    name: 'Lunar New Year',
    description: 'Red lanterns, gold accents, auspicious couplets',
    keywords: 'lunar new year house exterior, red paper lanterns at entrance, gold accents, spring couplets on door frame, blossom branches, festive east asian facade',
    season: { from: '01-15', to: '02-20' }
  },
  spring_blossom: {
    name: 'Spring Blossom',
    description: 'Blossoming trees, pastel planting, fresh light',
    keywords: 'spring house exterior, cherry blossom tree in front yard, tulip beds, pastel palette, fresh clear daylight, soft green lawn, seasonal',
    season: { from: '03-01', to: '05-10' }
  },
};

// Garden / landscape style set.
export const GARDEN_STYLES = {
  modern: {
    name: 'Modern Garden',
    description: 'Clipped geometry, concrete pavers, architectural grasses',
    keywords: 'modern garden, clipped hedges, large concrete pavers, architectural grasses, corten steel planter, linear layout, restrained planting'
  },
  zen: {
    name: 'Zen Garden',
    description: 'Raked gravel, stones, sparse plants, contemplative',
    keywords: 'zen garden, raked gravel, river stones, moss, japanese maple, minimalism',
  },
  cottage: {
    name: 'Cottage Garden',
    description: 'Romantic, dense planting, mixed perennials',
    keywords: 'cottage garden, dense perennials, foxglove, hollyhock, climbing roses, romantic',
  },
  desert: {
    name: 'Desert / Xeriscape',
    description: 'Drought-tolerant, succulents, gravel, rocks',
    keywords: 'xeriscape, succulents, agave, gravel, drought tolerant, southwest',
  },
  tropical: {
    name: 'Tropical',
    description: 'Lush foliage, big leaves, vivid blooms',
    keywords: 'tropical garden, palm, monstera, banana, hibiscus, lush foliage',
  },
  mediterranean: {
    name: 'Mediterranean',
    description: 'Gravel, olive and lavender, terracotta pots, pergola',
    keywords: 'mediterranean garden, gravel ground, olive tree, lavender, rosemary, terracotta pots, timber pergola, dry planting, warm sun'
  },
  coastal: {
    name: 'Coastal',
    description: 'Grasses, driftwood, salt-tolerant planting',
    keywords: 'coastal garden, ornamental grasses, driftwood, sea holly, pebble and sand, salt tolerant planting, weathered timber, breezy'
  },
  minimalist: {
    name: 'Minimalist',
    description: 'One material, one plant palette, negative space',
    keywords: 'minimalist garden, single material paving, one repeated plant, negative space, clean edges, restrained, quiet'
  },

  // ── 시즌 스타일 ────────────────────────────────────────────────────
  // interiorStyles 와 같은 창(window). 노출 판정은 AIService.isInSeason 이
  // 하고, 창은 Firestore config/seasons 로 덮어쓸 수 있다.
  // **키는 카테고리 간 동일해야 한다** — config/seasons 가 키 하나로 세
  // 카테고리를 한꺼번에 켜고 끈다.
  halloween: {
    name: 'Halloween',
    description: 'Pumpkin patch, lanterns, misty autumn garden',
    keywords: 'halloween garden, pumpkin patch, dried grasses and corn stalks, hanging lanterns on branches, black and amber, misty autumn evening, seasonal decor',
    season: { from: '09-15', to: '11-02' }
  },
  christmas: {
    name: 'Christmas',
    description: 'Lit evergreens, warm lights, winter garden',
    keywords: 'christmas garden, evergreen trees with warm string lights, wreaths, frosted shrubs, snow dusting, lantern path, festive winter garden, seasonal decor',
    season: { from: '11-20', to: '12-31' }
  },
  lunar_new_year: {
    name: 'Lunar New Year',
    description: 'Red lanterns, blossom branches, auspicious garden',
    keywords: 'lunar new year garden, red paper lanterns strung above, plum blossom branches, gold accents, stone path, festive east asian garden, seasonal',
    season: { from: '01-15', to: '02-20' }
  },
  spring_blossom: {
    name: 'Spring Blossom',
    description: 'Cherry blossom, tulips, fresh spring green',
    keywords: 'spring blossom garden, cherry blossom tree in bloom, tulip and daffodil beds, pastel palette, fresh green lawn, clear spring daylight, seasonal',
    season: { from: '03-01', to: '05-10' }
  },
  english: {
    name: 'English Garden',
    description: 'Manicured lawn, hedges, layered borders',
    keywords: 'english garden, manicured lawn, boxwood hedges, layered borders, formal',
  },
};

export const SEASON_OPTIONS = ['none', 'spring', 'summer', 'autumn', 'winter'];
export const TIME_OF_DAY_OPTIONS = ['none', 'day', 'golden_hour', 'night'];

export const SEASON_PROMPT = {
  spring: 'spring — new growth, blossoms, fresh greens',
  summer: 'high summer — full foliage, vibrant blooms, long shadows',
  autumn: 'autumn — warm tones, turning leaves, soft light',
  winter: 'winter — bare branches, muted palette, possible snow',
};

export const TIME_OF_DAY_PROMPT = {
  day: 'midday natural daylight, balanced exposure',
  golden_hour: 'late-afternoon golden hour, warm directional sun',
  night: 'evening / night, ambient lighting visible (path lights, accent fixtures)',
};

// Interior styles
export const INTERIOR_STYLES = {
    modern: {
      name: 'Modern',
      description: 'Clean lines, neutral tones, minimalist contemporary',
      keywords: 'modern, minimalist, clean lines, neutral colors, sleek furniture'
    },
    scandinavian: {
      name: 'Scandinavian',
      description: 'Bright, natural materials, cozy Nordic warmth',
      keywords: 'scandinavian, light wood, white walls, cozy, natural materials'
    },
    ikea: {
      name: 'IKEA',
      description: 'Functional, affordable, smart Scandinavian everyday design',
      keywords: 'ikea, functional, flat-pack, birch wood, white, practical, accessible nordic'
    },
    japandi: {
      name: 'Japandi',
      description: 'Japanese minimalism meets Scandinavian warmth',
      keywords: 'japandi, wabi-sabi, natural textures, muted palette, zen, functional minimalism'
    },
    industrial: {
      name: 'Industrial',
      description: 'Raw materials, exposed elements, urban loft aesthetic',
      keywords: 'industrial, exposed brick, metal, concrete, raw materials'
    },
    coastal: {
      name: 'Coastal',
      description: 'Breezy, light-filled beach house elegance',
      keywords: 'coastal, hampton, nautical, linen, white, blue, natural light, relaxed'
    },
    classic: {
      name: 'Classic',
      description: 'Timeless elegance, rich fabrics, ornate details',
      keywords: 'classic, elegant, traditional, ornate details, rich fabrics'
    },
    artdeco: {
      name: 'Art Deco',
      description: 'Bold geometry, gold accents, glamorous 1920s opulence',
      keywords: 'art deco, geometric, gold, black, velvet, glamorous, bold patterns'
    },
    midcentury: {
      name: 'Mid-Century Modern',
      description: 'Organic shapes, warm wood, 1950–60s retro-modern charm',
      keywords: 'mid-century modern, organic shapes, walnut, teak, retro, 1960s, eames'
    },
    maximalist: {
      name: 'Maximalist',
      description: 'Bold patterns, rich layers, more-is-more philosophy',
      keywords: 'maximalist, layered, bold color, pattern mixing, eclectic, rich textures'
    },
    vintage: {
      name: 'Vintage',
      description: 'Nostalgic charm, antique finds, warm retro palette',
      keywords: 'vintage, retro, antique furniture, warm colors, nostalgic'
    },
    bohemian: {
      name: 'Bohemian',
      description: 'Free-spirited, colorful textiles, artistic eclecticism',
      keywords: 'bohemian, eclectic, colorful textiles, plants, artistic'
    },
    tropical: {
      name: 'Tropical',
      description: 'Lush greenery, rattan, breezy resort feel',
      keywords: 'tropical, palm leaves, rattan, bamboo, lush plants, resort, light linen'
    },
    cottagecore: {
      name: 'Cottagecore',
      description: 'Romantic English countryside, florals, soft pastels',
      keywords: 'cottagecore, floral prints, lace, vintage countryside, pastel, gingham, antique'
    },
    rustic: {
      name: 'Rustic',
      description: 'Weathered wood, stone, warm farmhouse charm',
      keywords: 'rustic, reclaimed wood, stone, farmhouse, exposed beams, leather, warm earthy'
    },
    wabisabi: {
      name: 'Wabi-Sabi',
      description: 'Imperfect beauty, raw textures, quiet Japanese asceticism',
      keywords: 'wabi-sabi, raw plaster, hand-thrown ceramics, aged wood, muted earth, asymmetric'
    },
    gothic: {
      name: 'Gothic',
      description: 'Moody dark drama, velvet, ornate silhouettes',
      keywords: 'gothic, dark, velvet, ornate, candelabra, deep jewel tones, dramatic, baroque'
    },
    cyberpunk: {
      name: 'Cyberpunk',
      description: 'Neon-lit futurism, hi-tech surfaces, urban dystopia',
      keywords: 'cyberpunk, neon, RGB, futuristic, glossy black, magenta, cyan, sci-fi, hi-tech'
    },
    taisho_romance: {
      name: 'Taisho Romance',
      description: 'Early 20th-century Japan meets Western Art Nouveau — romantic and eclectic',
      keywords: 'taisho roman, taisho era, meiji modern, japanese-western fusion, tatami plus western chairs, stained glass, lace curtains, 1910s 1920s japan, romantic eclectic, art nouveau japan'
    },
    biophilic: {
      name: 'Biophilic',
      description: 'Plants everywhere, natural light, organic textures',
      keywords: 'biophilic design, indoor plants, monstera, fiddle leaf fig, natural wood, jute, linen, large windows, soft natural light, living wall'
    },
    cozy: {
      name: 'Cozy',
      description: 'Warm, soft, inviting — hygge-style comfort',
      keywords: 'cozy, hygge, warm lighting, candles, throw blankets, soft textures, layered rugs, neutral warm palette, inviting'
    },
    gaming: {
      name: 'Gaming',
      description: 'RGB-lit setup, sleek tech, immersive battlestation',
      keywords: 'gaming, RGB lighting, gaming chair, multi-monitor, dark walls, neon accents'
    },
    minimalist: {
      name: 'Minimalist',
      description: 'Pared back, low clutter, negative space',
      keywords: 'minimalist, pared back, uncluttered, negative space, monochrome palette, hidden storage, few objects'
    },
    mediterranean: {
      name: 'Mediterranean',
      description: 'Limewash walls, terracotta, arches, natural stone',
      keywords: 'mediterranean interior, limewash plaster walls, terracotta tile floor, arched doorway, natural stone, rattan, olive tones, warm sun'
    },
    luxury: {
      name: 'Quiet Luxury',
      description: 'Fine materials, restrained palette, nothing loud',
      keywords: 'quiet luxury, understated, cashmere, travertine, walnut, brushed brass, tonal neutral palette, no logos, tailored upholstery'
    },
    dark_academia: {
      name: 'Dark Academia',
      description: 'Moody library — oxblood, brass, stacked books',
      keywords: 'dark academia, library, floor to ceiling bookshelves, oxblood leather, brass desk lamp, deep green, moody, antique rug, wood paneling'
    },
    organic_modern: {
      name: 'Organic Modern',
      description: 'Modern forms in raw natural material',
      keywords: 'organic modern, plaster walls, raw oak, boucle, curved forms, linen, travertine, warm neutral, handmade ceramics'
    },
    farmhouse: {
      name: 'Farmhouse',
      description: 'Shiplap, apron sink, weathered wood, warm country',
      keywords: 'farmhouse interior, shiplap walls, apron front sink, weathered wood beams, black hardware, checkered textiles, country warmth'
    },

    // ── 시즌 스타일 ────────────────────────────────────────────────────
    // season 창(window) 밖에서는 목록에 안 뜬다 (styleCatalog 참고). 1년 내내
    // 걸어 두면 9월에 크리스마스 타일이 보여 잡음이 되고, 시즌에만 나타나야
    // "이맘때만 있는 것" 이라 다시 열어볼 이유가 된다.
    halloween: {
      name: 'Halloween',
      description: 'Pumpkins, candles, moody autumn dark',
      keywords: 'halloween decor, carved pumpkins, dried branches, black and amber, candlelight, cobweb lace, moody autumn, seasonal styling',
      season: { from: '09-15', to: '11-02' }
    },
    christmas: {
      name: 'Christmas',
      description: 'Tree, garlands, warm lights, festive layering',
      keywords: 'christmas decor, decorated tree, evergreen garland, warm string lights, stockings, red and gold, festive layering, seasonal styling',
      season: { from: '11-20', to: '12-31' }
    },
    lunar_new_year: {
      name: 'Lunar New Year',
      description: 'Red and gold, lanterns, auspicious motifs',
      keywords: 'lunar new year decor, red and gold, paper lanterns, blossom branch, auspicious motifs, festive east asian styling, seasonal',
      season: { from: '01-15', to: '02-20' }
    },
    spring_blossom: {
      name: 'Spring Blossom',
      description: 'Cherry blossom, pastels, fresh airy light',
      keywords: 'spring blossom decor, cherry blossom branches, pastel palette, sheer curtains, fresh airy light, tulips, light wood, seasonal styling',
      season: { from: '03-01', to: '05-10' }
    },

    custom: {
      name: 'Custom Style',
      description: 'Describe your own unique interior vision',
      keywords: 'custom, personalized, unique'
    },
    reference: {
      name: 'Reference',
      description: 'Match the tone, materials, and mood of a photo you upload',
      keywords: 'reference photo, match style, visual reference'
    },
    // 평면도 모드 카드 (2.4.0) — 3D 평면도 / 방 하나 보기 / 도면 정리. 도면 정리 결과의 style 값이기도 하다.
    floorplan: {
      name: 'Floor plan',
      description: 'Turn a floor plan into a 3D plan, one room, or a clean drawing',
      keywords: 'floor plan, 3d floor plan, room from plan, clean plan drawing'
    },
    cleanup: {
      name: 'Cleanup',
      description: 'Tidy up clutter or empty the room — your choice',
      keywords: 'cleanup, declutter, tidy up, empty room'
    },
    layout: {
      name: 'Layout',
      description: 'Same furniture, smarter placement — flow or spacious',
      keywords: 'layout, rearrange, flow, spacious, traffic'
    }
  };
